import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetTargetareListaVersiune } from "@/hooks/api/query/useGetTargetareListaVersiune";
import type { TargetareDimensiune } from "@/types/targetare";
import { ETICHETE_SURSA_VERSIUNE } from "@/utils/targetareConfig";
import {
  descarcaFisierText,
  formatDataOra,
  formatDimensiune,
  formatNumar,
} from "@/utils/targetareFormatting";
import { csvNepotrivite } from "@/utils/targetareListeParsare";

type VersiuneListaDialogProps = {
  versiuneId: number | null;
  onClose: () => void;
};

type Fila = "adaugat" | "eliminat" | "nepotrivite";

export function VersiuneListaDialog({
  versiuneId,
  onClose,
}: VersiuneListaDialogProps) {
  const versiune = useGetTargetareListaVersiune(versiuneId);
  const [fila, setFila] = useState<Fila>("adaugat");
  const v = versiune.data?.versiune;
  const diferente = useMemo(
    () => versiune.data?.diferente ?? [],
    [versiune.data]
  );
  const adaugate = diferente.filter((d) => d.fel === "adaugat");
  const eliminate = diferente.filter((d) => d.fel === "eliminat");
  const nepotrivite = v?.detalii?.nepotrivite ?? [];
  const curente =
    fila === "adaugat" ? adaugate : fila === "eliminat" ? eliminate : [];

  const descarca = () => {
    if (!v) return;
    const nume = `${v.nume_fisier.replace(/[^a-z0-9]+/gi, "-")}-v${v.id}-${fila}.csv`;
    if (fila === "nepotrivite") {
      descarcaFisierText(csvNepotrivite(nepotrivite), nume);
      return;
    }
    const linii = [
      "cui;denumire;dimensiune;tier;judet",
      ...curente.map((d) =>
        [
          d.cui ?? "",
          d.denumire ?? "",
          d.dimensiune ?? "",
          d.tier ?? "",
          d.judet ?? "",
        ]
          .map((x) => `"${String(x).replace(/"/g, '""')}"`)
          .join(";")
      ),
    ];
    descarcaFisierText(linii.join("\n"), nume);
  };

  return (
    <Dialog open={versiuneId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {v ? `Versiunea din ${formatDataOra(v.importat_la)}` : "Versiune"}
          </DialogTitle>
          <DialogDescription>
            {v ? (
              <>
                {v.nume_fisier} · {ETICHETE_SURSA_VERSIUNE[v.sursa] ?? v.sursa}
                {v.sursa_url && (
                  <>
                    {" · "}
                    <a
                      href={v.sursa_url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      sursa
                    </a>
                  </>
                )}
                {" · "}
                {formatNumar(v.randuri)} rânduri după import
                {v.inlocuire ? " (înlocuire)" : " (adăugare)"}
              </>
            ) : (
              "Se încarcă…"
            )}
          </DialogDescription>
        </DialogHeader>
        {versiune.isLoading ? (
          <Skeleton className="h-40" />
        ) : (
          v && (
            <>
              <dl className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
                {[
                  ["Adăugate", v.adaugate],
                  ["Eliminate", v.eliminate],
                  ["Neschimbate", v.neschimbate],
                  ["Rânduri fără firmă", v.nepotrivite],
                ].map(([k, n]) => (
                  <div key={String(k)} className="rounded-md border p-2">
                    <dt className="text-xs text-muted-foreground">{k}</dt>
                    <dd className="font-semibold tabular-nums">
                      {formatNumar(Number(n))}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="flex items-center justify-between gap-2">
                <Tabs value={fila} onValueChange={(f) => setFila(f as Fila)}>
                  <TabsList>
                    <TabsTrigger value="adaugat">
                      Adăugate ({adaugate.length})
                    </TabsTrigger>
                    <TabsTrigger value="eliminat">
                      Eliminate ({eliminate.length})
                    </TabsTrigger>
                    <TabsTrigger value="nepotrivite">
                      Fără firmă ({nepotrivite.length})
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={descarca}
                  disabled={
                    fila === "nepotrivite"
                      ? nepotrivite.length === 0
                      : curente.length === 0
                  }
                >
                  <Download className="mr-2 h-4 w-4" />
                  CSV
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-auto rounded border">
                {fila === "nepotrivite" ? (
                  nepotrivite.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      Toate rândurile au fost potrivite.
                    </p>
                  ) : (
                    <table className="w-full text-xs">
                      <tbody>
                        {nepotrivite.slice(0, 300).map((r, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1 font-mono">
                              {String(r.cui ?? "")}
                            </td>
                            <td className="px-2 py-1">
                              {String(r.denumire ?? "")}
                            </td>
                            <td className="max-w-[420px] truncate px-2 py-1 text-muted-foreground">
                              {Object.entries(r)
                                .filter(
                                  ([k]) =>
                                    ![
                                      "cui",
                                      "denumire",
                                      "cod_inmatriculare",
                                    ].includes(k)
                                )
                                .map(([k, val]) => `${k}: ${String(val)}`)
                                .join(" · ")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                ) : curente.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">
                    Nicio firmă în această categorie.
                  </p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted/60 text-left text-muted-foreground">
                      <tr>
                        <th className="px-2 py-1 font-medium">CUI</th>
                        <th className="px-2 py-1 font-medium">Denumire</th>
                        <th className="px-2 py-1 font-medium">Dimensiune</th>
                        <th className="px-2 py-1 font-medium">Tier</th>
                        <th className="px-2 py-1 font-medium">Județ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {curente.map((d, i) => (
                        <tr key={`${d.cui}-${i}`} className="border-t">
                          <td className="px-2 py-1 font-mono">
                            {d.cui ?? "–"}
                          </td>
                          <td className="px-2 py-1">{d.denumire ?? "–"}</td>
                          <td className="px-2 py-1">
                            {d.dimensiune
                              ? formatDimensiune(
                                  d.dimensiune as TargetareDimensiune
                                )
                              : "–"}
                          </td>
                          <td className="px-2 py-1">{d.tier ?? "–"}</td>
                          <td className="px-2 py-1">{d.judet ?? "–"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
